import { useEffect, useRef, useState } from 'react';
import { useSidebar } from '../context/SidebarContext';
import {
  Building,
  ChevronDown,
  Gift,
  Grid,
  KeyRound,
  MapPin,
  Megaphone,
  MoreVertical,
  Newspaper,
  NotepadText,
  Paperclip,
  SettingsIcon,
  Signpost,
  Sparkles,
  UserRoundPlus,
  Users,
  Wallpaper,
  Wrench,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import React from 'react';
import { useProfileStore } from '../../../stores/profileStore';
import ProfileAvatar from '../../common/ProfileAvatar';
import {
  AdminNavigationIcon,
  adminNavigationSections,
  findActiveAdminNavigationItem,
  findMatchingAdminSubItem,
  isAdminNavigationItemActive,
} from './adminNavigation';

const navItemIcons: Record<AdminNavigationIcon, React.ReactNode> = {
  dashboard: <Grid />,
  users: <Users />,
  projects: <Paperclip />,
  reports: <Signpost />,
  reviews: <NotepadText />,
  websiteContent: <Wallpaper />,
  serviceCatalog: <Sparkles />,
  campaigns: <Megaphone />,
  blog: <Newspaper />,
  services: <Wrench />,
  industries: <Building />,
  cities: <MapPin />,
  credits: <Gift />,
  administrator: <UserRoundPlus />,
  userLookup: <KeyRound />,
};

const AppSidebar: React.FC = () => {
  const { isExpanded, isMobileOpen, isHovered, setIsHovered } = useSidebar();
  const location = useLocation();
  const { t } = useTranslation(['common', 'admin']);

  const { profile } = useProfileStore();

  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);

  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef<HTMLDivElement | null>(null);
  const userMenuButtonRef = useRef<HTMLButtonElement | null>(null);

  const [subMenuHeight, setSubMenuHeight] = useState<Record<string, number>>({});
  const subMenuRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    const activeItem = findActiveAdminNavigationItem(location.pathname);
    setOpenSubmenu(activeItem?.subItemName ? activeItem.itemName : null);
  }, [location.pathname]);

  useEffect(() => {
    if (openSubmenu) {
      const key = `main-${openSubmenu}`;
      if (subMenuRefs.current[key]) {
        setSubMenuHeight(prevHeights => ({
          ...prevHeights,
          [key]: subMenuRefs.current[key]?.scrollHeight || 0,
        }));
      }
    }
  }, [openSubmenu]);

  // Close user menu when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (showUserMenu) {
        if (
          userMenuRef.current &&
          !userMenuRef.current.contains(target) &&
          userMenuButtonRef.current &&
          !userMenuButtonRef.current.contains(target)
        ) {
          setShowUserMenu(false);
        }
      }
    };

    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showUserMenu]);

  const handleSubmenuToggle = (itemName: string) => {
    setOpenSubmenu(currentItem => (currentItem === itemName ? null : itemName));
  };

  const username = `${profile?.first_name} ${profile?.last_name}`;
  const avatarUrl = profile?.avatar_url || '';

  const shouldShowContent = isExpanded || isHovered || isMobileOpen;

  return (
    <aside
      className={`fixed top-0 left-0 z-50 h-screen shadow-xl bg-white text-gray-900 transition-all duration-300 ease-in-out
      ${isExpanded || isMobileOpen ? 'w-[260px]' : isHovered ? 'w-[260px]' : 'w-[80px]'}
      ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}
      onMouseEnter={() => !isExpanded && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Logo */}
      <div
        className={`flex items-center py-6 px-4 border-b border-gray-100 ${
          !isExpanded && !isHovered ? 'lg:justify-center' : 'justify-start'
        }`}
      >
        <Link to="/admin" className="flex items-center gap-3">
          <img
            src="/android-chrome-192x192.png"
            alt="Logo"
            className={`${shouldShowContent ? 'h-6' : 'h-8 w-8'}`}
          />
          {shouldShowContent && (
            <span className="font-semibold text-lg text-gray-800">Pigeons</span>
          )}
        </Link>
      </div>

      {/* Nav */}
      <nav className="flex flex-col justify-between h-[calc(100vh-80px)] overflow-y-auto no-scrollbar p-4">
        <div className="flex-1">
          {adminNavigationSections.map(section => (
            <section
              key={section.name ?? 'overview'}
              className={section.name ? 'mt-5 first:mt-0' : undefined}
            >
              {section.name && shouldShowContent && (
                <h2 className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-gray-400">
                  {t(`navigation.sections.${section.name}`, { ns: 'admin' })}
                </h2>
              )}

              <ul className="flex flex-col gap-1">
                {section.items.map(nav => {
                  const isSubmenuOpen = openSubmenu === nav.name;
                  const isItemActive = isAdminNavigationItemActive(location.pathname, nav);
                  const activeSubItem = findMatchingAdminSubItem(location.pathname, nav);
                  const submenuKey = `main-${nav.name}`;
                  const label = t(`navigation.${nav.name}`, { ns: 'admin' });

                  return (
                    <li key={nav.name}>
                      {nav.subItems ? (
                        <>
                          <button
                            onClick={() => handleSubmenuToggle(nav.name)}
                            className={`flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-all duration-200 ${
                              isItemActive ? 'bg-gray-100 text-primary' : 'hover:bg-gray-200'
                            }`}
                            aria-label={label}
                            aria-expanded={isSubmenuOpen}
                            title={shouldShowContent ? undefined : label}
                          >
                            <span className="flex h-5 w-5 items-center justify-center">
                              {navItemIcons[nav.icon]}
                            </span>
                            {shouldShowContent && (
                              <>
                                <span className="flex-1 text-left">{label}</span>
                                <ChevronDown
                                  className={`h-4 w-4 transition-transform ${
                                    isSubmenuOpen ? 'rotate-180 text-brand-500' : ''
                                  }`}
                                />
                              </>
                            )}
                          </button>

                          {shouldShowContent && (
                            <div
                              ref={element => {
                                subMenuRefs.current[submenuKey] = element;
                              }}
                              className="overflow-hidden transition-all duration-300 ease-in-out"
                              style={{
                                height: isSubmenuOpen
                                  ? `${subMenuHeight[submenuKey] || 0}px`
                                  : '0px',
                              }}
                            >
                              <ul className="ml-6 mt-1 space-y-1 border-l border-gray-300 py-1 pl-3">
                                {nav.subItems.map(subItem => {
                                  const isSubItemActive = activeSubItem?.name === subItem.name;

                                  return (
                                    <li key={subItem.path}>
                                      <Link
                                        to={subItem.path}
                                        className={`relative flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-all duration-200 ${
                                          isSubItemActive
                                            ? 'bg-primary/10 font-medium text-primary'
                                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                        }`}
                                      >
                                        <span className="flex-1">
                                          {t(`navigation.${subItem.name}`, { ns: 'admin' })}
                                        </span>
                                        {isSubItemActive && (
                                          <div className="absolute -left-5 top-1/2 h-6 w-1 -translate-y-1/2 rounded-r-full bg-brand-500" />
                                        )}
                                      </Link>
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          )}
                        </>
                      ) : (
                        nav.path && (
                          <Link
                            to={nav.path}
                            className={`flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-all duration-200 ${
                              isItemActive ? 'bg-gray-100 text-primary' : 'hover:bg-gray-200'
                            }`}
                            aria-label={label}
                            title={shouldShowContent ? undefined : label}
                          >
                            <span className="flex h-5 w-5 items-center justify-center">
                              {navItemIcons[nav.icon]}
                            </span>
                            {shouldShowContent && <span>{label}</span>}
                          </Link>
                        )
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>

        {/* Bottom card */}
        <div className="mt-auto pt-4">
          <div className="rounded-xl border border-primary bg-gray-50 shadow-sm p-3 flex items-center justify-between gap-3 relative">
            {/* Avatar */}
            <ProfileAvatar
              first_name={profile?.first_name ?? ''}
              avatar_url={avatarUrl}
              className="w-9 h-9 text-sm font-medium bg-gray-300 text-gray-700"
            />

            <span className="text-sm font-medium text-gray-800 truncate">{username}</span>

            <button
              ref={userMenuButtonRef}
              onClick={() => setShowUserMenu(prev => !prev)}
              aria-expanded={showUserMenu}
              aria-haspopup="true"
              className="cursor-pointer hover:text-primary hover:bg-primary/20 rounded-full p-1"
            >
              <MoreVertical className="md:h-4 md:w-4" />
            </button>

            {/* Popover menu (links) */}
            {showUserMenu && (
              <div
                ref={userMenuRef}
                className="absolute bottom-14 right-2 z-50 w-44 bg-white rounded-lg shadow-xl border border-gray-100 p-2"
              >
                <Link
                  to="/admin/settings"
                  className="flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:text-primary hover:bg-gray-50 rounded"
                >
                  <SettingsIcon size={14} />
                  {t('navigation.settings', { ns: 'admin' })}
                </Link>
              </div>
            )}
          </div>
        </div>
      </nav>
    </aside>
  );
};

export default AppSidebar;
